import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Play, Pause, RefreshCw, Rss, Globe, Clock, AlertCircle, CheckCircle, Loader2, Settings, TestTube, Eye, Calendar, FileText } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { API_CONFIG } from '../../../config/api';
import toast from 'react-hot-toast';
import RssItemManagement from './RssItemManagement';

interface RssSource {
  id: string;
  name: string;
  description?: string;
  rss_url: string;
  website_url?: string;
  category: string;
  language: string;
  encoding: string;
  fetch_interval: number;
  max_items: number;
  content_selector?: string;
  auto_create_tasks: boolean;
  task_template_id?: string;
  task_priority: number;
  status: string;
  last_fetch_at?: string;
  last_fetch_status?: string;
  last_error?: string;
  next_fetch_at?: string;
  total_items_fetched: number;
  total_tasks_created: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  scrape_templates?: {
    id: string;
    name: string;
    platform: string;
  };
}

interface Template {
  id: string;
  name: string;
  platform: string;
}

interface FormData {
  name: string;
  description: string;
  rss_url: string;
  website_url: string;
  category: string;
  language: string;
  encoding: string;
  fetch_interval: number;
  max_items: number;
  content_selector: string;
  auto_create_tasks: boolean;
  task_template_id: string;
  task_priority: number;
}

const RssSourceManagement: React.FC = () => {
  const [sources, setSources] = useState<RssSource[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all'); // 新增搜索字段选择
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingSource, setEditingSource] = useState<RssSource | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fetching, setFetching] = useState<string | null>(null);
  
  // RSS条目管理状态
  const [showRssItems, setShowRssItems] = useState(false);
  const [selectedSource, setSelectedSource] = useState<{ id: string; name: string } | null>(null);
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    rss_url: '',
    website_url: '',
    category: 'general',
    language: 'zh-CN',
    encoding: 'UTF-8',
    fetch_interval: 3600,
    max_items: 50,
    content_selector: '',
    auto_create_tasks: true,
    task_template_id: '',
    task_priority: 5
  });

  const categories = [
    { value: 'general', label: '通用', color: 'gray' },
    { value: 'news', label: '新闻', color: 'blue' },
    { value: 'tech', label: '科技', color: 'green' },
    { value: 'business', label: '商业', color: 'purple' },
    { value: 'entertainment', label: '娱乐', color: 'pink' },
    { value: 'sports', label: '体育', color: 'orange' },
    { value: 'other', label: '其他', color: 'gray' }
  ];

  const statuses = [
    { value: 'active', label: '活跃', icon: CheckCircle, color: 'green' },
    { value: 'paused', label: '暂停', icon: Pause, color: 'yellow' },
    { value: 'error', label: '错误', icon: AlertCircle, color: 'red' },
    { value: 'disabled', label: '禁用', icon: Settings, color: 'gray' }
  ];

  useEffect(() => {
    fetchSources();
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=template_list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setTemplates(result.data.data || []);
      }
    } catch (error) {
      console.error('获取模板失败:', error);
    }
  };

  const fetchSources = async (page = currentPage, filters = {}) => {
    try {
      setLoading(true);
      const requestBody = {
        page,
        limit: pageSize,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        search: searchTerm || undefined,
        search_field: searchField !== 'all' ? searchField : undefined, // 修复：确保传递正确的搜索字段
        ...filters
      };

      // 添加调试日志
      console.log('🔍 前端发送的搜索参数:', {
        searchTerm,
        searchField,
        'search_field传递值': searchField !== 'all' ? searchField : undefined,
        requestBody
      });

      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=rss_list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setSources(result.data.data || []);
        setTotalCount(result.data.pagination?.total || 0);
        setTotalPages(result.data.pagination?.totalPages || 0);
        setCurrentPage(result.data.pagination?.page || 1);
      } else {
        console.error('获取RSS源失败:', result.error);
        toast.error('获取RSS源失败');
      }
    } catch (error) {
      console.error('获取RSS源失败:', error);
      toast.error('获取RSS源失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理分页变化
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchSources(page);
  };

  // 处理页面大小变化
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchSources(1);
  };

  // 处理筛选变化
  const handleFilterChange = () => {
    setCurrentPage(1);
    fetchSources(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);

      const requestData = {
        name: formData.name,
        description: formData.description,
        rss_url: formData.rss_url,
        website_url: formData.website_url,
        category: formData.category,
        language: formData.language,
        encoding: formData.encoding,
        fetch_interval: formData.fetch_interval,
        max_items: formData.max_items,
        content_selector: formData.content_selector,
        auto_create_tasks: formData.auto_create_tasks,
        task_template_id: formData.task_template_id || null,
        task_priority: formData.task_priority
      };

      if (editingSource) {
        // 更新RSS源
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=rss_update`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: editingSource.id,
            ...requestData
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('RSS源更新成功');
      } else {
        // 创建RSS源
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=rss_create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('RSS源创建成功');
      }

      await fetchSources();
      setShowForm(false);
      setEditingSource(null);
      resetForm();
    } catch (error) {
      console.error('保存RSS源失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`确定要删除RSS源「${name}」吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=rss_delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('RSS源删除成功');
      await fetchSources();
    } catch (error) {
      console.error('删除RSS源失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleStatusChange = async (source: RssSource, newStatus: string) => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=rss_update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: source.id,
          status: newStatus
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(`RSS源状态已更新为${getStatusInfo(newStatus).label}`);
      await fetchSources();
    } catch (error) {
      console.error('更新RSS源状态失败:', error);
      toast.error('状态更新失败：' + (error as Error).message);
    }
  };

  const handleTestRss = async (rssUrl: string) => {
    if (!rssUrl) {
      toast.error('请输入RSS URL');
      return;
    }

    try {
      setTesting(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=rss_test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rss_url: rssUrl
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(`RSS源测试成功！找到 ${result.data.itemCount} 个条目`);
    } catch (error) {
      console.error('RSS源测试失败:', error);
      toast.error('测试失败：' + (error as Error).message);
    } finally {
      setTesting(false);
    }
  };

  const handleFetchRss = async (sourceId: string) => {
    try {
      setFetching(sourceId);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=rss_fetch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_id: sourceId,
          immediate_fetch: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(`RSS拉取成功！处理了 ${result.data.itemsProcessed} 个条目，创建了 ${result.data.tasksCreated} 个任务`);
      await fetchSources();
    } catch (error) {
      console.error('RSS拉取失败:', error);
      toast.error('拉取失败：' + (error as Error).message);
    } finally {
      setFetching(null);
    }
  };

  const handleEdit = (source: RssSource) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      description: source.description || '',
      rss_url: source.rss_url,
      website_url: source.website_url || '',
      category: source.category,
      language: source.language,
      encoding: source.encoding,
      fetch_interval: source.fetch_interval,
      max_items: source.max_items,
      content_selector: source.content_selector || '',
      auto_create_tasks: source.auto_create_tasks,
      task_template_id: source.task_template_id || '',
      task_priority: source.task_priority
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      rss_url: '',
      website_url: '',
      category: 'general',
      language: 'zh-CN',
      encoding: 'UTF-8',
      fetch_interval: 3600,
      max_items: 50,
      content_selector: '',
      auto_create_tasks: true,
      task_template_id: '',
      task_priority: 5
    });
  };

  const handleViewItems = (source: RssSource) => {
    setSelectedSource({ id: source.id, name: source.name });
    setShowRssItems(true);
  };

  const handleBackFromItems = () => {
    setShowRssItems(false);
    setSelectedSource(null);
  };

  const getCategoryInfo = (category: string) => {
    return categories.find(c => c.value === category) || categories[0];
  };

  const getStatusInfo = (status: string) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  const formatInterval = (seconds: number) => {
    if (seconds < 60) return `${seconds}秒`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}小时`;
    return `${Math.floor(seconds / 86400)}天`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // 如果正在查看RSS条目，显示RSS条目管理组件
  if (showRssItems && selectedSource) {
    return (
      <RssItemManagement
        sourceId={selectedSource.id}
        sourceName={selectedSource.name}
        onBack={handleBackFromItems}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Rss className="w-6 h-6 mr-2 text-orange-600" />
              RSS源管理
            </h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => fetchSources()}
                disabled={loading}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                title="刷新RSS源列表"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </button>
              <button
                onClick={() => {
                  setEditingSource(null);
                  resetForm();
                  setShowForm(true);
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                添加RSS源
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={
                  searchField === 'name' ? '搜索RSS源名称...' :
                  searchField === 'description' ? '搜索RSS源描述...' :
                  searchField === 'rss_url' ? '搜索RSS URL...' :
                  '搜索RSS源名称、描述或URL...'
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleFilterChange();
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={searchField}
              onChange={(e) => {
                const newSearchField = e.target.value;
                console.log('🔄 搜索字段变更:', { from: searchField, to: newSearchField });
                setSearchField(newSearchField);
                // 如果有搜索词，立即触发搜索
                if (searchTerm) {
                  setTimeout(() => handleFilterChange(), 100);
                }
              }}
              className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部字段</option>
              <option value="name">仅名称</option>
              <option value="description">仅描述</option>
              <option value="rss_url">仅URL</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部分类</option>
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                onClick={handleFilterChange}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                title="查询"
              >
                <Search className="w-4 h-4 mr-2" />
                查询
              </button>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSearchField('all');
                  setStatusFilter('all');
                  setCategoryFilter('all');
                  setCurrentPage(1);
                  fetchSources(1, { search: '', search_field: undefined, status: undefined, category: undefined });
                }}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                title="重置"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                重置
              </button>
            </div>
          </div>

          {/* RSS源列表 - 表格格式 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RSS源信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    分类/状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    拉取配置
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    统计信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    最后拉取
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sources.map((source) => {
                  const categoryInfo = getCategoryInfo(source.category);
                  const statusInfo = getStatusInfo(source.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <tr key={source.id} className="hover:bg-gray-50">
                      {/* RSS源信息 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 rounded-lg bg-orange-100 mr-3 flex-shrink-0">
                            <Rss className="w-4 h-4 text-orange-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate" title={source.name}>
                              {source.name}
                            </div>
                            {source.description && (
                              <div className="text-sm text-gray-500 max-w-[200px] truncate" title={source.description}>
                                {source.description}
                              </div>
                            )}
                            <div className="text-xs text-gray-400 flex items-center mt-1">
                              <Globe className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate max-w-[180px]" title={source.rss_url}>{source.rss_url}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {/* 分类/状态 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${categoryInfo.color}-100 text-${categoryInfo.color}-800`}>
                            {categoryInfo.label}
                          </span>
                          <br />
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </span>
                        </div>
                      </td>
                      
                      {/* 拉取配置 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>间隔: {formatInterval(source.fetch_interval)}</div>
                          <div>最大条目: {source.max_items}</div>
                          <div>自动任务: {source.auto_create_tasks ? '是' : '否'}</div>
                        </div>
                      </td>
                      
                      {/* 统计信息 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>已拉取: {source.total_items_fetched}</div>
                          <div>已创建任务: {source.total_tasks_created}</div>
                        </div>
                      </td>
                      
                      {/* 最后拉取 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs text-gray-500">
                          {source.last_fetch_at ? (
                            <div className="space-y-1">
                              <div className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(source.last_fetch_at).toLocaleString()}
                              </div>
                              <div className={`text-xs ${source.last_fetch_status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {source.last_fetch_status === 'success' ? '成功' : '失败'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">从未拉取</span>
                          )}
                        </div>
                      </td>
                      
                      {/* 操作 */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {source.status === 'active' && (
                            <button
                              onClick={() => handleFetchRss(source.id)}
                              disabled={fetching === source.id}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                              title="立即拉取"
                            >
                              {fetching === source.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {source.status === 'active' && (
                            <button
                              onClick={() => handleStatusChange(source, 'paused')}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="暂停"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          {source.status === 'paused' && (
                            <button
                              onClick={() => handleStatusChange(source, 'active')}
                              className="text-green-600 hover:text-green-900"
                              title="启用"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewItems(source)}
                            className="text-purple-600 hover:text-purple-900"
                            title="查看条目"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(source)}
                            className="text-blue-600 hover:text-blue-900"
                            title="编辑"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(source.id, source.name)}
                            className="text-red-600 hover:text-red-900"
                            title="删除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {sources.length === 0 && !loading && (
            <div className="text-center py-12">
              <Rss className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无RSS源数据</p>
            </div>
          )}

          {/* 分页组件 */}
          {totalCount > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <span>每页显示</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span>条，共 {totalCount} 条</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    首页
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  
                  {/* 页码按钮 */}
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 text-sm border rounded ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    末页
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 表单弹窗 */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSource ? '编辑RSS源' : '添加RSS源'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RSS源名称 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入RSS源名称"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      分类
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      {categories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    RSS URL *
                  </label>
                  <div className="flex">
                    <input
                      type="url"
                      required
                      value={formData.rss_url}
                      onChange={(e) => setFormData({ ...formData, rss_url: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://example.com/rss.xml"
                    />
                    <button
                      type="button"
                      onClick={() => handleTestRss(formData.rss_url)}
                      disabled={testing || !formData.rss_url}
                      className="px-4 py-2 bg-gray-100 text-gray-700 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                      title="测试RSS源"
                    >
                      {testing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    网站URL
                  </label>
                  <input
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    描述
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入RSS源描述"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      语言
                    </label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="zh-CN">中文</option>
                      <option value="en">English</option>
                      <option value="ja">日本語</option>
                      <option value="ko">한국어</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      编码
                    </label>
                    <select
                      value={formData.encoding}
                      onChange={(e) => setFormData({ ...formData, encoding: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="UTF-8">UTF-8</option>
                      <option value="GBK">GBK</option>
                      <option value="GB2312">GB2312</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      任务优先级 (1-10)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.task_priority}
                      onChange={(e) => setFormData({ ...formData, task_priority: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      拉取间隔 (秒)
                    </label>
                    <input
                      type="number"
                      min="60"
                      max="86400"
                      value={formData.fetch_interval}
                      onChange={(e) => setFormData({ ...formData, fetch_interval: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      当前设置: {formatInterval(formData.fetch_interval)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      最大条目数
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={formData.max_items}
                      onChange={(e) => setFormData({ ...formData, max_items: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    内容选择器 (可选)
                  </label>
                  <input
                    type="text"
                    value={formData.content_selector}
                    onChange={(e) => setFormData({ ...formData, content_selector: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder=".content, .article-body, p"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    用于提取文章全文的CSS选择器
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.auto_create_tasks}
                      onChange={(e) => setFormData({ ...formData, auto_create_tasks: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">自动创建采集任务</span>
                  </label>
                  {formData.auto_create_tasks && (
                    <select
                      value={formData.task_template_id}
                      onChange={(e) => setFormData({ ...formData, task_template_id: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">选择任务模板 (可选)</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} ({template.platform})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingSource(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {submitting ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RssSourceManagement;